import { useMemo, useState } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CreditCard, Loader2, X, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const CARD_STYLE = { style: { base: { fontSize: "16px", color: "#1A242B", fontFamily: "inherit", "::placeholder": { color: "#8FA89B" } }, invalid: { color: "#dc2626" } } };

function Inner({ clientSecret, intakeId, amount, name, onClose, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const pay = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      const res = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: elements.getElement(CardElement), billing_details: { name: name || "Deposit" } },
      });
      if (res.error) throw new Error(res.error.message);
      if (res.paymentIntent && res.paymentIntent.status === "succeeded") {
        await api.post(`/pos/intake/${intakeId}/deposit/confirm`);
        toast.success("Deposit charged");
        onPaid();
        return;
      }
      throw new Error("Payment did not complete.");
    } catch (e) {
      toast.error(e.response?.data?.detail ? formatApiError(e.response.data.detail) : (e.message || "Payment failed."));
    } finally { setLoading(false); }
  };

  return (
    <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7" data-testid="deposit-card-modal">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CreditCard size={22} className="text-[#144F75]" /> Deposit</h3>
        <button onClick={onClose} data-testid="deposit-card-cancel" className="text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
      </div>
      <p className="text-sm text-[#4A5D6B] mb-4">Charge <span className="font-semibold text-[#144F75]">{money(Number(amount))}</span>{name ? ` · ${name}` : ""}</p>
      <div className="rounded-xl border border-[#E5E0D8] px-3.5 py-3.5 bg-white mb-4" data-testid="deposit-card-element"><CardElement options={CARD_STYLE} /></div>
      <button onClick={pay} disabled={loading || !stripe} data-testid="deposit-card-pay" className="eyw-btn w-full justify-center disabled:opacity-60">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={16} />} Charge {money(Number(amount))}
      </button>
      <p className="text-[11px] text-[#8FA89B] text-center mt-3 flex items-center justify-center gap-1"><Lock size={11} /> Entered securely in Stripe — never stored on our servers.</p>
    </div>
  );
}

export default function DepositCardModal({ clientSecret, publishableKey, intakeId, amount, name, onClose, onPaid }) {
  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center px-4 bg-black/40 backdrop-blur-sm">
      {stripePromise ? (
        <Elements stripe={stripePromise}><Inner clientSecret={clientSecret} intakeId={intakeId} amount={amount} name={name} onClose={onClose} onPaid={onPaid} /></Elements>
      ) : (
        <div className="bg-white rounded-2xl p-6 max-w-sm text-center"><p className="text-sm text-[#4A5D6B]">Stripe is not configured.</p><button onClick={onClose} className="eyw-btn mt-4">Close</button></div>
      )}
    </div>
  );
}
